const collapsedBlocks = new Set<string>()
const userExpanded = new Set<string>()

const delegated = new WeakSet<HTMLElement>()

const CHECK_ICON =
  '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>'

export function createCodeBlockId(lang: string, content: string): string {
  return `cb-${hashCode(lang)}-${hashCode(content)}`
}

function hashCode(str: string): string {
  let h = 0
  for (let i = 0; i < str.length; i++) {
    h = (h << 5) - h + str.charCodeAt(i)
    h |= 0
  }
  return (h >>> 0).toString(36)
}

export function setupCodeBlockActions(container: HTMLElement): void {
  if (!delegated.has(container)) {
    delegated.add(container)
    container.addEventListener('click', handleClick)
  }
  restoreCollapsed(container)
}

function handleClick(event: MouseEvent): void {
  const target = event.target as HTMLElement
  const copyBtn = target.closest<HTMLElement>('.code-block__copy')
  const downloadBtn = target.closest<HTMLElement>('.code-block__download')
  const block = target.closest<HTMLElement>('.code-block')
  if (!block) return

  if (copyBtn) {
    event.stopPropagation()
    copyBlock(block, copyBtn)
    return
  }
  if (downloadBtn) {
    event.stopPropagation()
    downloadBlock(block)
    return
  }

  const selection = window.getSelection()
  if (selection && !selection.isCollapsed) return
  toggleCollapse(block)
}

function toggleCollapse(block: HTMLElement): void {
  const id = block.dataset.blockId
  const collapsed = block.classList.toggle('code-block--collapsed')
  if (id) {
    if (collapsed) {
      collapsedBlocks.add(id)
      userExpanded.delete(id)
    } else {
      collapsedBlocks.delete(id)
      userExpanded.add(id)
    }
  }
}

function restoreCollapsed(container: HTMLElement): void {
  container.querySelectorAll<HTMLElement>('.code-block__header').forEach((block) => {
    const id = block.dataset.blockId
    if (!id) return
    if (userExpanded.has(id)) {
      block.classList.remove('code-block--collapsed')
    } else if (collapsedBlocks.has(id)) {
      block.classList.add('code-block--collapsed')
    }
  })
}

function codeOf(block: HTMLElement): string {
  const pre = block.querySelector<HTMLElement>('.code-block__scroll pre')
  return pre ? pre.textContent ?? '' : ''
}

function copyBlock(block: HTMLElement, button: HTMLElement): void {
  const text = codeOf(block)
  if (navigator.clipboard?.writeText) {
    navigator.clipboard
      .writeText(text)
      .then(() => flashButton(button, '', true))
      .catch(() => fallbackCopy(text, button))
  } else {
    fallbackCopy(text, button)
  }
}

function fallbackCopy(text: string, button: HTMLElement): void {
  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.style.position = 'fixed'
  textarea.style.opacity = '0'
  document.body.appendChild(textarea)
  textarea.select()
  try {
    document.execCommand('')
    flashButton(button, '', true)
  } catch {
    flashButton(button, '复制失败', false)
  } finally {
    document.body.removeChild(textarea)
  }
}

function downloadBlock(block: HTMLElement): void {
  const text = codeOf(block)
  const lang = block.dataset.lang || 'txt'
  const ext = lang === 'plaintext' || lang === 'text' ? 'txt' : lang.split(/\s+/)[0]
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `code.${ext}`
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

function flashButton(button: HTMLElement, text: string, ok: boolean): void {
  const original = button.dataset.original || button.innerHTML
  button.dataset.original = original
  button.innerHTML = ok ? `${CHECK_ICON}` : text
  button.classList.add(ok ? 'is-success' : 'is-error')
  window.setTimeout(() => {
    button.innerHTML = original
    button.classList.remove('is-success', 'is-error')
  }, 1500)
}